import React from "react";
import * as Yup from "yup";
import {
  Box,
  TextField,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";

export const customerTaxOptions = ["Taxable", "Non Taxable"];

const trimValue = (value) => String(value || "").trim();

export const formatCustomerPhone = (value) => {
  const digits = String(value || "")
    .replace(/\D/g, "")
    .slice(0, 10);
  if (digits.length <= 3) return digits ? `(${digits}` : "";
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const normalizeMiddleInitial = (value) =>
  String(value || "")
    .replace(/[^a-z]/gi, "")
    .slice(0, 1)
    .toUpperCase();

const splitLegacyCustomerName = (name, companyName = "") => {
  const fullName = trimValue(name);
  const company = trimValue(companyName);

  if (!fullName || (company && fullName === company)) {
    return { first_name: "", middle_initial: "", last_name: "" };
  }

  const parts = fullName.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return {
      first_name: parts[0],
      middle_initial: "",
      last_name: "",
    };
  }

  return {
    first_name: parts[0],
    middle_initial: normalizeMiddleInitial(parts.slice(1, -1)[0]),
    last_name: parts[parts.length - 1],
  };
};

export const buildCustomerName = (value = {}) => {
  const personName = [
    trimValue(value.first_name),
    normalizeMiddleInitial(value.middle_initial),
    trimValue(value.last_name),
  ]
    .filter(Boolean)
    .join(" ");

  return personName || trimValue(value.company_name) || trimValue(value.name);
};

export const createCustomerInitialValues = (userId = "") => ({
  customer_type: "individual",
  name: "",
  first_name: "",
  middle_initial: "",
  last_name: "",
  email: "",
  phone: "",
  address: "",
  zip: "",
  city: "",
  state: "",
  company_name: "",
  dealer_no: "",
  tax_exempt_number: "",
  tax: "Taxable",
  taxValue: 0,
  discountIncluded: false,
  discountType: "",
  discount: 0,
  user_id: userId,
});

export const resolveCustomerType = (value = {}) => {
  const explicitType = trimValue(value.customer_type).toLowerCase();
  const hasCompany = Boolean(trimValue(value.company_name));
  const hasPersonName = Boolean(
    trimValue(value.first_name) ||
      trimValue(value.middle_initial) ||
      trimValue(value.last_name),
  );

  if (explicitType === "business") return "business";
  if (explicitType === "individual" && (!hasCompany || hasPersonName)) {
    return "individual";
  }

  return hasCompany && !hasPersonName ? "business" : "individual";
};

export const prepareCustomerFormValues = (value = {}, userId = "") => {
  const explicitNames =
    trimValue(value.first_name) ||
    trimValue(value.middle_initial) ||
    trimValue(value.last_name);
  const parsedNames = explicitNames
    ? {
        first_name: trimValue(value.first_name),
        middle_initial: normalizeMiddleInitial(value.middle_initial),
        last_name: trimValue(value.last_name),
      }
    : splitLegacyCustomerName(value.name, value.company_name);

  return {
    ...createCustomerInitialValues(userId),
    ...value,
    customer_type: resolveCustomerType(value),
    ...parsedNames,
    name: trimValue(value.name),
    phone: formatCustomerPhone(value.phone),
    company_name: trimValue(value.company_name),
    dealer_no: trimValue(value.dealer_no),
    tax_exempt_number: trimValue(value.tax_exempt_number),
    email: trimValue(value.email),
    address: value.address || "",
    zip: trimValue(value.zip),
    city: trimValue(value.city),
    state: trimValue(value.state),
    tax: value.tax || "Taxable",
    taxValue:
      typeof value.taxValue !== "undefined" && value.taxValue !== null
        ? value.taxValue
        : 0,
    discountIncluded: Boolean(value.discountIncluded),
    discountType: value.discountType || "",
    discount: Number(value.discount) || 0,
    user_id: value.user_id || userId,
  };
};

export const buildCustomerPayload = (value = {}, userId = "") => {
  const prepared = prepareCustomerFormValues(value, userId);
  const customerType = resolveCustomerType(prepared);
  const customerName =
    customerType === "business"
      ? trimValue(prepared.company_name) ||
        trimValue(prepared.name) ||
        buildCustomerName(prepared)
      : buildCustomerName(prepared);

  return {
    ...prepared,
    customer_type: customerType,
    first_name: trimValue(prepared.first_name),
    middle_initial: normalizeMiddleInitial(prepared.middle_initial),
    last_name: trimValue(prepared.last_name),
    company_name: trimValue(prepared.company_name),
    dealer_no: trimValue(prepared.dealer_no),
    tax_exempt_number: trimValue(prepared.tax_exempt_number),
    email: trimValue(prepared.email),
    address: prepared.address || "",
    zip: trimValue(prepared.zip),
    city: trimValue(prepared.city),
    state: trimValue(prepared.state),
    phone: formatCustomerPhone(prepared.phone),
    name: customerName,
    discountIncluded: Boolean(
      prepared.discountIncluded &&
        prepared.discountType &&
        Number(prepared.discount) > 0,
    ),
    discountType:
      prepared.discountIncluded && Number(prepared.discount) > 0
        ? prepared.discountType
        : "",
    discount:
      prepared.discountIncluded && prepared.discountType
        ? Math.max(0, Number(prepared.discount) || 0)
        : 0,
  };
};

const customerNameMessage = "Enter first name or provide company name";

export const customerValidationSchema = Yup.object({
  customer_type: Yup.string().oneOf(["individual", "business"]).required(),
  first_name: Yup.string().test("customer-first-or-company", customerNameMessage, function (value) {
    const firstName = trimValue(value);
    const companyName = trimValue(this.parent.company_name);
    const legacyName = trimValue(this.parent.name);
    const customerType = trimValue(this.parent.customer_type) || "individual";
    if (customerType === "business") {
      return Boolean(companyName || legacyName);
    }
    return Boolean(firstName || companyName || legacyName);
  }),
  middle_initial: Yup.string()
    .transform((value) => normalizeMiddleInitial(value))
    .max(1, "Use one letter only"),
  last_name: Yup.string(),
  company_name: Yup.string().test(
    "customer-company-required-for-business",
    "Company name is required",
    function (value) {
      const customerType = trimValue(this.parent.customer_type) || "individual";
      if (customerType !== "business") return true;
      return Boolean(trimValue(value) || trimValue(this.parent.name));
    },
  ),
  email: Yup.string().email("Invalid email format"),
  phone: Yup.string()
    .required("Phone is required")
    .matches(/^[0-9+\-\s()]+$/, "Invalid phone number"),
  address: Yup.string(),
  zip: Yup.string().test(
    "zip-format",
    "ZIP must be 5 digits",
    (value) => {
      const zip = trimValue(value);
      return !zip || /^\d{5}$/.test(zip);
    },
  ),
  city: Yup.string(),
  state: Yup.string()
    .max(2, "Use 2-letter state code")
    .transform((value) => String(value || "").toUpperCase()),
  dealer_no: Yup.string(),
  tax_exempt_number: Yup.string(),
  tax: Yup.string()
    .required("Tax status is required")
    .oneOf(customerTaxOptions, "Invalid tax status"),
  discountIncluded: Yup.boolean(),
  discountType: Yup.string().when("discountIncluded", {
    is: true,
    then: (schema) =>
      schema
        .required("Discount type is required")
        .oneOf(["Percentage", "Amount"], "Invalid discount type"),
    otherwise: (schema) => schema.notRequired(),
  }),
  discount: Yup.number()
    .transform((value, originalValue) =>
      String(originalValue).trim() === "" ? 0 : value,
    )
    .when("discountIncluded", {
      is: true,
      then: (schema) =>
        schema
          .min(0, "Discount cannot be negative")
          .test(
            "percentage-max",
            "Percentage discount cannot exceed 100",
            function (value) {
              if (this.parent.discountType !== "Percentage") return true;
              return Number(value) <= 100;
            },
          ),
      otherwise: (schema) => schema.notRequired(),
    }),
});

function CustomerAddressFields({ values, setFieldValue, errors, touched }) {
  const [zipLookupLoading, setZipLookupLoading] = React.useState(false);
  const lastResolvedZipRef = React.useRef("");
  const requestIdRef = React.useRef(0);

  const runZipLookup = React.useCallback(async () => {
    const zip = String(values.zip || "")
      .replace(/\D/g, "")
      .slice(0, 5);

    if (!/^\d{5}$/.test(zip) || zip === lastResolvedZipRef.current) {
      return;
    }

    const requestId = ++requestIdRef.current;
    setZipLookupLoading(true);

    try {
      const response = await fetch(`https://api.zippopotam.us/us/${zip}`);
      if (!response.ok) return;
      const data = await response.json();
      if (requestId !== requestIdRef.current) return;
      const place = Array.isArray(data?.places) ? data.places[0] : null;
      if (!place) return;

      lastResolvedZipRef.current = zip;
      setFieldValue("zip", zip);
      setFieldValue("city", place["place name"] || "");
      setFieldValue("state", place["state abbreviation"] || "");
    } catch (_error) {
      // Leave manual entry available if lookup fails.
    } finally {
      if (requestId === requestIdRef.current) {
        setZipLookupLoading(false);
      }
    }
  }, [setFieldValue, values.zip]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <TextField
        fullWidth
        size="small"
        label="Address"
        value={values.address || ""}
        onChange={(e) => setFieldValue("address", e.target.value)}
        error={touched.address && Boolean(errors.address)}
        helperText={(touched.address && errors.address) || ""}
      />
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1.15fr 1.2fr 0.55fr" },
          gap: 2,
        }}
      >
        <TextField
          fullWidth
          size="small"
          label="ZIP"
          value={values.zip || ""}
          onChange={(e) => {
            lastResolvedZipRef.current = "";
            setFieldValue("zip", String(e.target.value || "").replace(/\D/g, "").slice(0, 5));
          }}
          onBlur={runZipLookup}
          error={touched.zip && Boolean(errors.zip)}
          helperText={(touched.zip && errors.zip) || "ZIP can auto-fill city/state"}
          inputProps={{ inputMode: "numeric", maxLength: 5 }}
          InputProps={{
            endAdornment: zipLookupLoading ? (
              <InputAdornment position="end">
                <CircularProgress size={16} />
              </InputAdornment>
            ) : null,
          }}
        />
        <TextField
          fullWidth
          size="small"
          label="City"
          value={values.city || ""}
          onChange={(e) => setFieldValue("city", e.target.value)}
          error={touched.city && Boolean(errors.city)}
          helperText={(touched.city && errors.city) || ""}
        />
        <TextField
          fullWidth
          size="small"
          label="State"
          value={values.state || ""}
          onChange={(e) =>
            setFieldValue("state", String(e.target.value || "").toUpperCase().slice(0, 2))
          }
          error={touched.state && Boolean(errors.state)}
          helperText={(touched.state && errors.state) || ""}
          inputProps={{ maxLength: 2, style: { textTransform: "uppercase" } }}
        />
      </Box>
    </Box>
  );
}

export const getCustomerFormFields = () => [
  {
    name: "customer_type",
    type: "custom",
    component: ({ values, setFieldValue }) => (
      <Tabs
        value={values.customer_type || "individual"}
        onChange={(_, nextValue) => setFieldValue("customer_type", nextValue)}
        variant="fullWidth"
        sx={{
          minHeight: 44,
          borderBottom: "1px solid",
          borderColor: "divider",
          "& .MuiTab-root": {
            minHeight: 44,
            textTransform: "none",
            fontWeight: 600,
          },
        }}
      >
        <Tab value="individual" label="Individual" />
        <Tab value="business" label="Business" />
      </Tabs>
    ),
  },
  {
    name: "phone",
    type: "custom",
    component: ({ values, setFieldValue, errors, touched }) => (
      <TextField
        fullWidth
        size="small"
        label="Phone"
        value={values.phone || ""}
        onChange={(e) => setFieldValue("phone", formatCustomerPhone(e.target.value))}
        error={touched.phone && Boolean(errors.phone)}
        helperText={(touched.phone && errors.phone) || ""}
      />
    ),
  },
  {
    name: "customer_name_group",
    type: "custom",
    component: ({ values, setFieldValue, errors, touched }) => (
      values.customer_type === "business" ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField
            fullWidth
            size="small"
            label="Business Name"
            value={values.company_name || ""}
            onChange={(e) => setFieldValue("company_name", e.target.value)}
            error={touched.company_name && Boolean(errors.company_name)}
            helperText={(touched.company_name && errors.company_name) || ""}
          />
          <TextField
            fullWidth
            size="small"
            label="Dealer No"
            value={values.dealer_no || ""}
            onChange={(e) => setFieldValue("dealer_no", e.target.value)}
            error={touched.dealer_no && Boolean(errors.dealer_no)}
            helperText={(touched.dealer_no && errors.dealer_no) || ""}
          />
          <TextField
            fullWidth
            size="small"
            label="Tax Exempt Number"
            value={values.tax_exempt_number || ""}
            onChange={(e) => setFieldValue("tax_exempt_number", e.target.value)}
            error={
              touched.tax_exempt_number &&
              Boolean(errors.tax_exempt_number)
            }
            helperText={
              (touched.tax_exempt_number && errors.tax_exempt_number) || ""
            }
          />
        </Box>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr 2fr" },
            gap: 2,
          }}
        >
          <TextField
            fullWidth
            size="small"
            label="First Name"
            value={values.first_name || ""}
            onChange={(e) => setFieldValue("first_name", e.target.value)}
            error={touched.first_name && Boolean(errors.first_name)}
            helperText={(touched.first_name && errors.first_name) || ""}
          />
          <TextField
            fullWidth
            size="small"
            label="Middle Initial"
            value={values.middle_initial || ""}
            onChange={(e) =>
              setFieldValue(
                "middle_initial",
                normalizeMiddleInitial(e.target.value),
              )
            }
            error={touched.middle_initial && Boolean(errors.middle_initial)}
            helperText={(touched.middle_initial && errors.middle_initial) || ""}
            inputProps={{ maxLength: 1, style: { textTransform: "uppercase" } }}
          />
          <TextField
            fullWidth
            size="small"
            label="Last Name (Optional)"
            value={values.last_name || ""}
            onChange={(e) => setFieldValue("last_name", e.target.value)}
            error={touched.last_name && Boolean(errors.last_name)}
            helperText={(touched.last_name && errors.last_name) || ""}
          />
        </Box>
      )
    ),
  },
  { name: "email", label: "Email", type: "email" },
  {
    name: "customer_address_group",
    type: "custom",
    component: ({ values, setFieldValue, errors, touched }) => (
      <CustomerAddressFields
        values={values}
        setFieldValue={setFieldValue}
        errors={errors}
        touched={touched}
      />
    ),
  },
  {
    name: "tax",
    type: "custom",
    component: ({ values, setFieldValue, errors, touched }) => (
      <TextField
        select
        fullWidth
        size="small"
        label="Tax Status"
        value={values.tax || "Taxable"}
        onChange={(e) => setFieldValue("tax", e.target.value)}
        error={touched.tax && Boolean(errors.tax)}
        helperText={(touched.tax && errors.tax) || ""}
      >
        {customerTaxOptions.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>
    ),
  },
  {
    name: "discount_group",
    type: "custom",
    component: ({ values, setFieldValue, errors, touched }) => (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        <TextField
          select
          fullWidth
          size="small"
          label="Default Discount"
          value={values.discountIncluded ? values.discountType || "Percentage" : ""}
          onChange={(e) => {
            const discountType = e.target.value;
            setFieldValue("discountIncluded", Boolean(discountType));
            setFieldValue("discountType", discountType);
            if (!discountType) {
              setFieldValue("discount", 0);
            }
          }}
          error={touched.discountType && Boolean(errors.discountType)}
          helperText={(touched.discountType && errors.discountType) || ""}
        >
          <MenuItem value="">No discount</MenuItem>
          <MenuItem value="Percentage">Percentage</MenuItem>
          <MenuItem value="Amount">Dollar amount</MenuItem>
        </TextField>
        <TextField
          fullWidth
          size="small"
          type="number"
          label={values.discountType === "Percentage" ? "Discount %" : "Discount $"}
          value={values.discount || ""}
          onChange={(e) => setFieldValue("discount", e.target.value)}
          disabled={!values.discountIncluded}
          error={touched.discount && Boolean(errors.discount)}
          helperText={(touched.discount && errors.discount) || ""}
          inputProps={{
            min: 0,
            max: values.discountType === "Percentage" ? 100 : undefined,
          }}
        />
      </Box>
    ),
  },
];
